import React, { useEffect, useState } from 'react';
import {
  buildCrossAiSummary,
  buildPlatformAiSummary,
  contextAffiliation,
  contextPriorityChip,
  contextSourceChips,
  type WorkThread,
  type WorkThreadSourceKind,
} from '../../domain/radarThreads';
import { BrandIcon, Pill } from '../../ui/common';

interface RadarWorkThreadCardProps {
  thread: WorkThread;
  isExpanded: boolean;
  onToggle: (thread: WorkThread) => void;
  onRequestAttach?: (thread: WorkThread) => void;
  isAttachOpen?: boolean;
  onPassToAssistant?: (thread: WorkThread) => void;
  isPassedToAssistant?: boolean;
  onSnooze: (thread: WorkThread, durationMs: number) => void;
}

function PriorityBell({ tone }: { tone: 'urgent' | 'important' | 'neutral' }) {
  const color =
    tone === 'urgent' ? 'var(--z-bad)' : tone === 'important' ? 'var(--z-warn)' : 'var(--z-dim)';
  return (
    <span style={{ color, display: 'inline-flex' }} aria-hidden>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </svg>
    </span>
  );
}

function platformTabLabel(source: WorkThreadSourceKind, sourceLabel?: string): string {
  if (source === 'agents' && sourceLabel) return sourceLabel;
  switch (source) {
    case 'jira':
      return 'Jira';
    case 'github':
      return 'GitHub';
    case 'slack':
      return 'Slack';
    case 'gmail':
      return 'Gmail';
    case 'agents':
      return 'Agente';
    case 'notes':
      return 'Notas';
    case 'reminders':
      return 'Recordatorios';
    default:
      return source;
  }
}

export const RadarWorkThreadCard: React.FC<RadarWorkThreadCardProps> = React.memo(
  ({
    thread,
    isExpanded,
    onToggle,
    onRequestAttach,
    isAttachOpen = false,
    onPassToAssistant,
    isPassedToAssistant = false,
    onSnooze,
  }) => {
    const affiliation = contextAffiliation(thread);
    const priority = contextPriorityChip(thread);
    const chips = contextSourceChips(thread);
    const [focusedSource, setFocusedSource] = useState<WorkThreadSourceKind | null>(null);
    const [snoozeOpen, setSnoozeOpen] = useState(false);

    const sourceKey = thread.sources.map((src) => src.source).join(',');

    useEffect(() => {
      if (!isExpanded) {
        setFocusedSource(null);
        return;
      }
      setFocusedSource((current) => {
        if (current && sourceKey.split(',').includes(current)) return current;
        return null;
      });
    }, [isExpanded, thread.id, sourceKey]);

    const summaryText = focusedSource
      ? buildPlatformAiSummary(thread, focusedSource)
      : buildCrossAiSummary(thread);
    const summarySections = [
      {
        source: focusedSource ?? thread.sources[0]?.source ?? 'jira',
        label: focusedSource ? platformTabLabel(focusedSource) : 'Resumen general',
        text: summaryText,
      },
    ];

    const borderLeftColor =
      priority === 'urgent'
        ? 'var(--z-bad)'
        : priority === 'important'
          ? 'var(--z-warn)'
          : thread.statusTone === 'ok'
            ? 'var(--z-ok)'
            : 'var(--z-accent-2)';

    if (!isExpanded) {
      return (
        <article
          onClick={() => onToggle(thread)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle(thread);
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={false}
          data-testid={`radar-thread-collapsed-${thread.id}`}
          className="z-radar-context-card z-radar-context-card--collapsed"
        >
          <PriorityBell tone={priority ?? 'neutral'} />
          <span className="z-radar-context-key">{thread.key}</span>
          <span className="z-radar-context-title">{thread.title}</span>
          <Pill tone={affiliation === 'mine' ? 'accent' : 'flat'}>
            {affiliation === 'mine' ? 'Mía' : 'Ajena'}
          </Pill>
          {priority && (
            <Pill tone={priority === 'urgent' ? 'bad' : 'warn'}>
              {priority === 'urgent' ? 'Urgente' : 'Importante'}
            </Pill>
          )}
          <span className="z-radar-context-age">{thread.updatedText}</span>
        </article>
      );
    }

    return (
      <article
        data-testid={`radar-thread-expanded-${thread.id}`}
        className="z-radar-context-card z-radar-context-card--active"
        style={{ borderLeftColor }}
        aria-expanded={true}
      >
        <div className="z-radar-context-body">
          <header className="z-radar-context-header">
            <div className="z-radar-context-heading">
              <PriorityBell tone={priority ?? 'neutral'} />
              <h3>
                <span className="z-radar-context-key">{thread.key}</span>
                <span className="z-radar-context-sep"> · </span>
                <span className="z-radar-context-title">{thread.title}</span>
              </h3>
              <Pill tone={affiliation === 'mine' ? 'accent' : 'flat'}>
                {affiliation === 'mine' ? 'Mía' : 'Ajena'}
              </Pill>
              {priority && (
                <Pill tone={priority === 'urgent' ? 'bad' : 'warn'}>
                  {priority === 'urgent' ? 'Urgente' : 'Importante'}
                </Pill>
              )}
            </div>
            <div className="z-radar-context-meta">
              <span className="z-radar-context-signal-dot" data-tone={priority ?? 'neutral'} />
              <span>Última señal {thread.updatedText.replace(/^Actualizado\s+/i, '')}</span>
              <button
                type="button"
                className="z-radar-context-collapse"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(thread);
                }}
                title="Contraer"
                aria-label="Contraer contexto"
                data-testid={`radar-thread-collapse-${thread.id}`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                >
                  <polyline points="18 15 12 9 6 15" />
                </svg>
              </button>
            </div>
          </header>

          <div
            className="z-radar-context-platform-tabs"
            role="tablist"
            aria-label="Plataformas del contexto"
            data-testid={`radar-thread-tabs-${thread.id}`}
          >
            {chips.map((chip) => {
              const selected = focusedSource === chip.source;
              const sourceMeta = thread.sources.find((src) => src.source === chip.source);
              const label = platformTabLabel(chip.source, sourceMeta?.label);
              return (
                <button
                  key={chip.source}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-label={`Mostrar resumen de ${label}`}
                  title={label}
                  data-testid={`radar-thread-tab-${thread.id}-${chip.source}`}
                  className={`z-radar-context-platform-tab${selected ? ' is-active' : ''}`}
                  onClick={() =>
                    setFocusedSource((current) => (current === chip.source ? null : chip.source))
                  }
                >
                  <BrandIcon source={chip.source} size={15} />
                  <span className="z-radar-context-platform-tab-label">{label}</span>
                </button>
              );
            })}
          </div>

          <div className="z-radar-context-summaries z-radar-context-summaries--single">
            <div className="z-radar-context-summary-block">
              <span className="z-radar-context-summary-label">Resumen del contexto (IA)</span>
              <div className="z-radar-context-summary-sections">
                {summarySections.map((section, index) => (
                  <div
                    key={`${section.source}-${index}`}
                    className="z-radar-context-summary-section"
                  >
                    <span className="z-radar-context-summary-source">{section.label}</span>
                    <p data-testid={index === 0 ? `radar-thread-focused-${thread.id}` : undefined}>
                      {section.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="z-radar-context-next">
            <strong>Qué sigue</strong>
            <span aria-hidden>→</span>
            <span>{thread.nextStep}</span>
          </div>
        </div>

        <aside className="z-radar-context-actions">
          {onPassToAssistant && (
            <button
              type="button"
              className="z-radar-context-link"
              data-testid={`radar-pass-ai-${thread.id}`}
              onClick={() => onPassToAssistant(thread)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden
              >
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              <span>{isPassedToAssistant ? 'En el asistente' : 'Pasar al asistente'}</span>
            </button>
          )}
          <div className="z-radar-context-snooze-wrap">
            <button
              type="button"
              className="z-radar-context-snooze"
              aria-expanded={snoozeOpen}
              onClick={() => setSnoozeOpen((open) => !open)}
              data-testid={`radar-snooze-${thread.id}`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              Posponer
            </button>
            {snoozeOpen && (
              <div
                className="z-radar-context-snooze-menu"
                data-testid={`radar-snooze-menu-${thread.id}`}
              >
                {[
                  { label: 'En 1 hora', durationMs: 60 * 60 * 1000 },
                  { label: 'Mañana', durationMs: 24 * 60 * 60 * 1000 },
                  { label: 'En 1 semana', durationMs: 7 * 24 * 60 * 60 * 1000 },
                ].map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => {
                      onSnooze(thread, option.durationMs);
                      setSnoozeOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {onRequestAttach && thread.isManual && (
            <button
              type="button"
              data-testid={`radar-attach-${thread.id}`}
              className="z-radar-context-link"
              onClick={() => onRequestAttach(thread)}
            >
              {isAttachOpen ? 'Cerrar picker' : 'Añadir recurso'}
            </button>
          )}
        </aside>
      </article>
    );
  },
);
